import React, {useState} from 'react';
import {ControlButton, TscButtonType, Glyphs, TscButtonGlyphPlacement, TscStyles, TscThemeNames} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import config from '../../service/config';
import {IComment} from '../../model/commentModel';
import UserMessaging from '../../util/UserMessaging';
import conversationApi from '../../service/conversationApi';
import styled from 'styled-components';
import {SetupWorkerState} from '../../model/workerState';
import Constants from '../../constants/Constants';
import {updateCommentsState, commentsStore} from '../../context/commentsProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import {CommentFooterWrapper} from '../util/StyledElements';
import {disableConversationStore} from '../../context/disableConversationProvider';
import {themeStore} from '../../context/themeProvider';
import logging from '../../service/logging';
import {updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';

export const LikeButtonWrapper = styled.div<{isReply: boolean; userHasLikedComment: boolean}>`
   width: 4.5rem;
   display: flex;
   justify-content: ${props => props.isReply ? 'flex-start' : 'center'};

   button {
      margin: ${props => props.isReply && props.userHasLikedComment ? '0 0.5rem 0 .5rem !important' : props.isReply ? '0 0.5rem 0 0 !important' : '0 0.5rem !important'};
   }
`;

export const LikeListWrapper = styled.div<{isReply: boolean}>`
   position: absolute;
   margin-top: 2.875rem;
   margin-left: ${props => props.isReply ? '.3125rem' : '0'};
   background-color: black;
   opacity: 0.9;
   padding: 0.75rem 0 0.75rem 0.75rem;
   border-radius: 0.25rem;
   z-index: 1;
   color: ${TscStyles.color.text.light};
   max-width: 15rem;
`;

const LikeList = styled.div`
   display: grid;
   max-height: 19.75rem;
   overflow-y: auto;

   span {
      margin-right: 0.75rem;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
   }
`;

const ListArrow = styled.div<{isReply: boolean}>`
   position: absolute;
   margin-top: 2.125rem;
   margin-left: ${props => props.isReply ? '1.25rem' : '0'};
   width: 0;
   height: 0;
   border-left: .75rem solid transparent;
   border-right: .75rem solid transparent;
   border-bottom: .75rem solid black;
   opacity: 0.9;
`;

export const NonEditingCommentFooterBase: React.FC<INonEditingCommentFooterProps & IStateMappedProps> = ({comment, isReply, userHasLikedComment, comments, isCommentingDisabled, handleExpandReplies, theme}) => {
   const {t} = useTranslation();
   const [loadingState, setLoadingState] = SetupWorkerState();
   const [showListOfLikes, setShowListOfLikes] = useState(false);

   const onReplyClick = () => {
      handleExpandReplies();

      window.setTimeout(() => {
         document.getElementById(`${comment.id}-reply-input`)?.focus();
      }, Constants.getDomElementTimeout);
   };

   const onLikeClick = async (e: React.MouseEvent) => {
      e.stopPropagation();

      if (config.user.TechSmithId === Constants.nullTechSmithId) {
         updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: true});
         return;
      }

      setLoadingState({isWorking: true, hasError: false});

      try {
         const updatedComment = userHasLikedComment ? await conversationApi.unlikeComment(comment, config.user.TechSmithId) : await conversationApi.likeComment(comment, config.user.TechSmithId);
         const commentsCopy = Object.assign([], comments);

         if (isReply) {
            const parentComment = commentsCopy.find((c: IComment) => c.id === updatedComment.parentCommentId);
            parentComment.replies.splice(parentComment.replies.findIndex((r: IComment) => r.id === updatedComment.id), 1, updatedComment);
            commentsCopy.splice(commentsCopy.findIndex((oldComment: IComment) => oldComment.id === parentComment.id), 1, parentComment);
         } else {
            commentsCopy.splice(commentsCopy.findIndex((oldComment: IComment) => oldComment.id === comment.id), 1, updatedComment);
         }

         updateCommentsState({comments: commentsCopy});
         setLoadingState({isWorking: false, hasError: false});
      } catch (err) {
         logging.error('Failed to like comment', err);
         UserMessaging.warn(t('conversations.failedSaveComment'));
         setLoadingState({isWorking: false, hasError: true});
      }
   };

   const onEditCommentClick = () => {
      document.getElementById('comment-editing-alert').style.display = 'block';
      const commentsCopy = Object.assign([] as IComment[], comments);

      if (isReply) {
         updateCommentsState({comments: commentsCopy.map(c => {
            if (c.id !== comment.parentCommentId) {
               return c;
            }
            return Object.assign({}, c, {replies: c.replies.map((r: IComment) => r.id === comment.id ? Object.assign({}, r, {isEditing: true}) : r)}) as IComment;
         })});
      } else {
         updateCommentsState({comments: commentsCopy.map(c => {
            if (c.id === comment.id) {
               return Object.assign({}, c, {isEditing: true}) as IComment;
            }
            return c;
         })});
      }

      setTimeout(() => {
         document.getElementById('comment-editing-alert').style.display = 'none';
      }, 1000);
   };

   const showEditButton = (config.watchConfig.isOwnerOrAdmin && !comment.isDeleted) || comment.authorId === config.user.TechSmithId;

   return (
      <CommentFooterWrapper isReply={isReply} isEditing={false} data-test-id="non-editing-comment-footer">
         {!isReply &&
            <ControlButton
               onClick={onReplyClick}
               label={t('conversations.reply')}
               themeName={theme}
               buttonType={TscButtonType.floating}
               disabled={isCommentingDisabled}
               testId="comment-reply-btn"
            />
         }
         {!comment.isDeleted &&
            <LikeButtonWrapper isReply={isReply} userHasLikedComment={userHasLikedComment} onMouseLeave={() => setShowListOfLikes(false)} onMouseEnter={() => setShowListOfLikes(true)}>
               <ControlButton
                  onClick={onLikeClick}
                  disabled={loadingState.isWorking || comment.isDeleted || isCommentingDisabled}
                  ariaPressed={userHasLikedComment}
                  buttonType={userHasLikedComment ? TscButtonType.primary : TscButtonType.floating}
                  themeName={theme}
                  label={comment.likes.length.toString()}
                  glyph={<Glyphs.ThumbsUp16x16/>}
                  glyphPlacement={TscButtonGlyphPlacement.left}
                  ariaLabel={t('conversations.likeCommentLabel', {count: comment.likes.length})}
                  testId="comment-like-btn"
               />
               {showListOfLikes && !!comment.likes.length && <>
                  <ListArrow isReply={isReply} />
                  <LikeListWrapper isReply={isReply}>
                     <LikeList>
                        {comment.likes.map(like => <span key={like.UserId}>{like.DisplayName}</span>)}
                     </LikeList>
                  </LikeListWrapper>
               </>}
            </LikeButtonWrapper>
         }
         {showEditButton &&
            <ControlButton
               onClick={onEditCommentClick}
               label={t('conversations.edit')}
               themeName={theme}
               buttonType={TscButtonType.floating}
               disabled={isCommentingDisabled}
               testId="comment-edit-btn"
            />
         }
      </CommentFooterWrapper>
   );
};

export interface INonEditingCommentFooterProps {
   comment: IComment;
   isReply: boolean;
   userHasLikedComment: boolean;
   handleExpandReplies(): void;
}

export interface IStateMappedProps {
   comments: IComment[];
   isCommentingDisabled: boolean;
   theme: TscThemeNames;
}

const mapStatesToProps = (
   {comment, isReply, userHasLikedComment, handleExpandReplies}: INonEditingCommentFooterProps,
   {comments}: Partial<IStateMappedProps>,
   {isCommentingDisabled}: Partial<IStateMappedProps>,
   {theme}: Partial<IStateMappedProps>
): INonEditingCommentFooterProps & IStateMappedProps => ({comment, isReply, userHasLikedComment, comments, isCommentingDisabled, handleExpandReplies, theme});

export default withMemoizedContexts(mapStatesToProps, commentsStore, disableConversationStore, themeStore)(NonEditingCommentFooterBase);
